/**
 * The equipment register: every vehicle and item of plant on the airport, the
 * airport's own and each stakeholder's.
 *
 * Sweepers, water blasters, friction testers, tankers, loaders, graders,
 * access platforms, dump trucks, fire appliances, generators and ground
 * support equipment. What is in the register is not the interesting part; what
 * is expiring in it is.
 *
 * ── why this is not the asset register ──────────────────────────────────
 *
 * The platform's asset register holds what is fixed: runways, circuits,
 * lights, chillers, pumps. The airports pack declares no vehicle type at all,
 * so a sweeper is not an asset on this platform and has nowhere to keep a
 * driver, a registration or a licence expiry. Until it does, this register is
 * entered data held under its own note topic, attributed and dated like the
 * finance figures and the drill log.
 *
 * The shape below is deliberately the shape an asset would take — a tag, a
 * type, a location, a condition, attributes and dates — so the day the pack
 * declares vehicle types this becomes a read of `/v1/assets` and the screen
 * above it does not change.
 *
 * ── what makes this an airport's register rather than a fleet list ──────
 *
 * Three dates, and the third is the one a generic fleet system does not hold:
 *
 *  - the vehicle licence, which is the State's;
 *  - the safety inspection, which is the operator's;
 *  - the **airside vehicle permit**, and the driver's **airside driving
 *    permit** with it, which are the aerodrome's. A pickup with a current
 *    licence, a clean inspection and a lapsed airside permit is roadworthy,
 *    insured and not allowed on the manoeuvring area, and it is the only one
 *    of the three that stops the vehicle at the gate.
 *
 * Every expiry is read against one clock — the `now` passed in — and an item
 * with no date recorded is reported as unknown rather than as compliant. A
 * blank in a fleet register reads as "fine" to every reader who has not
 * checked, which is how a lapsed permit reaches the manoeuvring area.
 *
 * Pure. The page gathers, this decides.
 */
import { t, tn } from "../i18n";
import type { SourceRef } from "./decision";
import type { FleetChange } from "./fleetEdit";

/* ── what a thing is ──────────────────────────────────────────────────── */

export type FleetKind =
  "sweeper" | "surface" | "tanker" | "earthmoving" | "access" | "haulage" | "rescue" | "power" | "gse" | "vehicle";

export const FLEET_KINDS: { key: FleetKind; label: string; what: string }[] = [
  { key: "sweeper", label: "Sweepers", what: "runway, taxiway and road sweepers, and the magnetic bars" },
  {
    key: "surface",
    label: "Surface plant",
    what: "water blasters, friction testers, line marking units, rubber removal",
  },
  { key: "tanker", label: "Tankers", what: "water tankers, fuel bowsers, vacuum tankers" },
  { key: "earthmoving", label: "Earthmoving", what: "bobcats, backhoes, graders, loaders, excavators" },
  { key: "access", label: "Access platforms", what: "scissor lifts, boom trucks, forklifts, telehandlers" },
  { key: "haulage", label: "Haulage", what: "dump trucks, flatbeds, low loaders, tippers" },
  { key: "rescue", label: "Rescue and fire", what: "fire appliances, rapid intervention, ambulances" },
  { key: "power", label: "Power and lighting", what: "generators, light towers, ground power units" },
  { key: "gse", label: "Ground support", what: "tugs, belt loaders, steps, air start, chillers" },
  { key: "vehicle", label: "Vehicles", what: "cars, pickups, vans, crew buses, follow-me" },
];

export const KIND_LABEL: Record<FleetKind, string> = Object.fromEntries(
  FLEET_KINDS.map((k) => [k.key, k.label]),
) as Record<FleetKind, string>;

export type Condition = "serviceable" | "degraded" | "awaiting_parts" | "unserviceable" | "unknown";

/**
 * Worst first. The register is read to find what is wrong with it.
 *
 * `awaiting_parts` sits beside `unserviceable` and not with `degraded`,
 * because the machine is equally off the road — but it is kept APART from
 * unserviceable because the two need different work. An unserviceable machine
 * is a job somebody can schedule. One waiting for parts is a job nobody can
 * schedule, and the constraint is a supplier rather than a crew, so it belongs
 * on a different list and in front of a different person.
 */
export const CONDITION_ORDER: Condition[] = ["unserviceable", "awaiting_parts", "degraded", "unknown", "serviceable"];

export const CONDITION_LABEL: Record<Condition, string> = {
  serviceable: "Serviceable",
  degraded: "Degraded",
  awaiting_parts: "Awaiting parts",
  unserviceable: "Unserviceable",
  unknown: "Not assessed",
};

/** Off the road, for either of the two reasons. */
export const isDown = (c: Condition): boolean => c === "unserviceable" || c === "awaiting_parts";

export interface Owner {
  /** the airport itself, or a named third party working on it */
  kind: "airport" | "stakeholder";
  name: string;
}

export interface MaintenanceEntry {
  at: string;
  what: string;
  kind: "scheduled" | "repair" | "inspection" | "defect";
  by?: string;
  hours?: number;
  cost?: number;
  /** a work order on the platform, where the job was raised as one */
  workOrder?: string;
}

export interface Driver {
  name: string;
  /** the aerodrome's airside driving permit, not a road licence */
  permit?: string;
  permitExpiry?: string;
}

export interface FleetItem {
  id: string;
  /** the fleet number painted on it */
  tag: string;
  name: string;
  kind: FleetKind;
  owner: Owner;
  make?: string;
  model?: string;
  year?: number;
  condition: Condition;
  conditionNote?: string;
  /** a zone code where the platform knows one, else where it is kept */
  location?: string;
  /** the department it belongs to */
  department?: string;
  /** the department using it, where that is not the one that owns it */
  usedByDepartment?: string;
  driver?: Driver;
  registration?: string;
  /** the State's vehicle licence */
  licenceExpiry?: string;
  insuranceExpiry?: string;
  /** the aerodrome's own permit to take it airside */
  airsidePermit?: { ref?: string; expiry?: string };
  safetyInspection?: { lastAt?: string; nextDueAt?: string; result?: string };
  hoursRun?: number;
  maintenance?: MaintenanceEntry[];
  /** off the working register, kept with its history and its tag; see fleetEdit.ts */
  retired?: { at: string; by: string; reason: string };
}

export interface FleetRegister {
  items: FleetItem[];
  /** every change ever made to the register, oldest first, append-only — see fleetEdit.ts */
  changes?: FleetChange[];
  enteredBy?: string;
  enteredAt?: string;
}

/* ── what the register says about each item ───────────────────────────── */

export type FlagKind = "expired" | "expiring" | "overdue" | "missing" | "down";

export interface Flag {
  kind: FlagKind;
  what: string;
  /** days until, negative when past; null when there is no date at all */
  days: number | null;
}

export interface FleetRow {
  item: FleetItem;
  flags: Flag[];
  /** the worst flag, for sorting and for the row's tone */
  worst: FlagKind | null;
  /** days to the soonest expiry of any kind; null when nothing is dated */
  soonestDays: number | null;
  lastMaintenanceAt: string | null;
  maintenanceCount: number;
}

const DAY = 86_400_000;

/** The group an item with no department falls into, named rather than blank. */
export const UNASSIGNED_DEPARTMENT = "No department recorded";

/** Within this many days, an expiry is worth acting on rather than noting. */
export const EXPIRING_SOON_DAYS = 30;

function daysUntil(date: string | undefined, now: Date): number | null {
  if (!date) return null;
  const at = Date.parse(date.length <= 10 ? `${date}T00:00:00Z` : date);
  if (Number.isNaN(at)) return null;
  return Math.floor((at - now.getTime()) / DAY);
}

/**
 * Every reason this item should not simply be driven away.
 *
 * A missing date is its own flag and is never silently treated as met. The
 * register is read by somebody deciding whether a vehicle can go airside, and
 * "no expiry recorded" and "expires next year" must not look the same.
 */
export function flagsFor(item: FleetItem, now: Date): Flag[] {
  const out: Flag[] = [];

  const dated = (date: string | undefined, what: string, missing: string) => {
    const days = daysUntil(date, now);
    if (days === null) out.push({ kind: "missing", what: missing, days: null });
    else if (days < 0) out.push({ kind: "expired", what, days });
    else if (days <= EXPIRING_SOON_DAYS) out.push({ kind: "expiring", what, days });
  };

  dated(item.licenceExpiry, "vehicle licence", "no vehicle licence expiry recorded");
  dated(item.airsidePermit?.expiry, "airside vehicle permit", "no airside permit expiry recorded");
  dated(item.insuranceExpiry, "insurance", "no insurance expiry recorded");

  // The driver's permit, which stops the vehicle as surely as the vehicle's own.
  if (item.driver) {
    dated(item.driver.permitExpiry, "driver's airside permit", "no driver permit expiry recorded");
  } else {
    out.push({ kind: "missing", what: "no driver assigned", days: null });
  }

  const due = daysUntil(item.safetyInspection?.nextDueAt, now);
  if (item.safetyInspection?.nextDueAt == null)
    out.push({ kind: "missing", what: "no safety inspection scheduled", days: null });
  else if (due !== null && due < 0) out.push({ kind: "overdue", what: "safety inspection", days: due });
  else if (due !== null && due <= EXPIRING_SOON_DAYS)
    out.push({ kind: "expiring", what: "safety inspection", days: due });

  if (isDown(item.condition))
    out.push({
      kind: "down",
      what: item.conditionNote ?? (item.condition === "awaiting_parts" ? "awaiting parts" : "unserviceable"),
      days: null,
    });

  return out;
}

const FLAG_RANK: Record<FlagKind, number> = { down: 0, expired: 1, overdue: 2, missing: 3, expiring: 4 };

export function rowFor(item: FleetItem, now: Date): FleetRow {
  const flags = flagsFor(item, now);
  const worst = flags.length ? [...flags].sort((a, b) => FLAG_RANK[a.kind] - FLAG_RANK[b.kind])[0].kind : null;
  const dates = [
    item.licenceExpiry,
    item.airsidePermit?.expiry,
    item.insuranceExpiry,
    item.driver?.permitExpiry,
    item.safetyInspection?.nextDueAt,
  ]
    .map((d) => daysUntil(d, now))
    .filter((d): d is number => d !== null);
  const log = [...(item.maintenance ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  return {
    item,
    flags,
    worst,
    soonestDays: dates.length ? Math.min(...dates) : null,
    lastMaintenanceAt: log[0]?.at ?? null,
    maintenanceCount: log.length,
  };
}

/* ── sorting, which is what the screen is for ─────────────────────────── */

export type SortKey =
  "type" | "condition" | "location" | "driver" | "licence" | "inspection" | "maintenance" | "owner" | "department";

export const SORTS: { key: SortKey; label: string }[] = [
  { key: "type", label: "Type" },
  { key: "condition", label: "Condition" },
  { key: "location", label: "Location" },
  { key: "driver", label: "Driver assigned" },
  { key: "licence", label: "License expiry" },
  { key: "inspection", label: "Safety inspection" },
  { key: "maintenance", label: "Maintenance" },
  { key: "owner", label: "Owner" },
  { key: "department", label: "Department" },
];

/**
 * Sort, putting what is wrong or unknown first in every order.
 *
 * A register sorted by licence expiry that puts the items with NO licence date
 * at the bottom has hidden the worst rows behind the ones it can measure.
 * Undated and unassigned sort first here, ahead of the soonest date, on every
 * key where the absence is itself the finding.
 */
export function sortFleet(rows: readonly FleetRow[], by: SortKey): FleetRow[] {
  const missingFirst = (a: number | null, b: number | null) =>
    a === null && b === null ? 0 : a === null ? -1 : b === null ? 1 : a - b;
  const text = (a: string | undefined, b: string | undefined) =>
    a === undefined && b === undefined ? 0 : a === undefined ? -1 : b === undefined ? 1 : a.localeCompare(b);

  const copy = [...rows];
  switch (by) {
    case "type":
      return copy.sort(
        (a, b) =>
          KIND_LABEL[a.item.kind].localeCompare(KIND_LABEL[b.item.kind]) || a.item.tag.localeCompare(b.item.tag),
      );
    case "condition":
      return copy.sort(
        (a, b) =>
          CONDITION_ORDER.indexOf(a.item.condition) - CONDITION_ORDER.indexOf(b.item.condition) ||
          a.item.tag.localeCompare(b.item.tag),
      );
    case "location":
      return copy.sort((a, b) => text(a.item.location, b.item.location) || a.item.tag.localeCompare(b.item.tag));
    case "driver":
      return copy.sort(
        (a, b) => text(a.item.driver?.name, b.item.driver?.name) || a.item.tag.localeCompare(b.item.tag),
      );
    case "licence":
      return copy.sort(
        (a, b) =>
          missingFirst(daysUntilCached(a, "licence"), daysUntilCached(b, "licence")) ||
          a.item.tag.localeCompare(b.item.tag),
      );
    case "inspection":
      return copy.sort(
        (a, b) =>
          missingFirst(daysUntilCached(a, "inspection"), daysUntilCached(b, "inspection")) ||
          a.item.tag.localeCompare(b.item.tag),
      );
    case "maintenance":
      // Longest since anything was done, first; never maintained before that.
      return copy.sort((a, b) => {
        if (a.lastMaintenanceAt === null && b.lastMaintenanceAt === null) return a.item.tag.localeCompare(b.item.tag);
        if (a.lastMaintenanceAt === null) return -1;
        if (b.lastMaintenanceAt === null) return 1;
        return a.lastMaintenanceAt.localeCompare(b.lastMaintenanceAt);
      });
    case "department":
      /* Unassigned first. A vehicle no department owns is the one that gets
         neither maintained nor renewed, and in a list sorted with the blanks
         at the bottom it is the one nobody scrolls to. */
      return copy.sort((a, b) => text(a.item.department, b.item.department) || a.item.tag.localeCompare(b.item.tag));
    case "owner":
      return copy.sort(
        (a, b) =>
          a.item.owner.name.localeCompare(b.item.owner.name) ||
          KIND_LABEL[a.item.kind].localeCompare(KIND_LABEL[b.item.kind]),
      );
    default:
      return copy;
  }
}

/** The two date sorts read their own field rather than the row's soonest. */
function daysUntilCached(row: FleetRow, which: "licence" | "inspection"): number | null {
  const raw = which === "licence" ? row.item.licenceExpiry : row.item.safetyInspection?.nextDueAt;
  if (!raw) return null;
  const at = Date.parse(raw.length <= 10 ? `${raw}T00:00:00Z` : raw);
  return Number.isNaN(at) ? null : at;
}

/* ── the desk ─────────────────────────────────────────────────────────── */

export interface FleetInputs {
  now: Date;
  siteCode: string;
  register: FleetRegister;
}

export interface FleetDesk {
  generatedAt: string;
  rows: FleetRow[];
  byKind: { kind: FleetKind; label: string; count: number; down: number }[];
  owners: { name: string; kind: Owner["kind"]; count: number; down: number }[];
  departments: { name: string; count: number; down: number }[];
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
  enteredBy?: string;
  enteredAt?: string;
}

export function buildFleetDesk(inp: FleetInputs): FleetDesk {
  const rows = inp.register.items.map((item) => rowFor(item, inp.now));
  const has = (row: FleetRow, kind: FlagKind, what?: string) =>
    row.flags.some((f) => f.kind === kind && (what === undefined || f.what.includes(what)));

  const down = rows.filter((r) => isDown(r.item.condition));
  const awaitingParts = rows.filter((r) => r.item.condition === "awaiting_parts");
  const expiredLicence = rows.filter((r) => has(r, "expired", "vehicle licence"));
  const expiredPermit = rows.filter((r) => has(r, "expired", "airside"));
  const soonLicence = rows.filter((r) => has(r, "expiring", "vehicle licence"));
  const overdueInspection = rows.filter((r) => has(r, "overdue", "safety inspection"));
  const noDriver = rows.filter((r) => !r.item.driver);
  const stakeholder = rows.filter((r) => r.item.owner.kind === "stakeholder");
  const undated = rows.filter((r) => r.flags.some((f) => f.kind === "missing"));

  const byKind = FLEET_KINDS.map((k) => ({
    kind: k.key,
    label: k.label,
    count: rows.filter((r) => r.item.kind === k.key).length,
    down: rows.filter((r) => r.item.kind === k.key && isDown(r.item.condition)).length,
  })).filter((k) => k.count > 0);

  const ownerMap = new Map<string, { name: string; kind: Owner["kind"]; count: number; down: number }>();
  for (const row of rows) {
    const key = row.item.owner.name;
    const entry = ownerMap.get(key) ?? { name: key, kind: row.item.owner.kind, count: 0, down: 0 };
    entry.count += 1;
    if (isDown(row.item.condition)) entry.down += 1;
    ownerMap.set(key, entry);
  }
  const deptMap = new Map<string, { name: string; count: number; down: number }>();
  for (const row of rows) {
    // An item with no department is grouped under one, rather than dropped:
    // "nobody's" is the group that most needs to be looked at, and a register
    // that only lists the departments it knows about hides it.
    const key = row.item.department ?? UNASSIGNED_DEPARTMENT;
    const entry = deptMap.get(key) ?? { name: key, count: 0, down: 0 };
    entry.count += 1;
    if (isDown(row.item.condition)) entry.down += 1;
    deptMap.set(key, entry);
  }
  const departments = [...deptMap.values()].sort(
    (a, b) =>
      (a.name === UNASSIGNED_DEPARTMENT ? -1 : b.name === UNASSIGNED_DEPARTMENT ? 1 : 0) ||
      b.count - a.count ||
      a.name.localeCompare(b.name),
  );

  const owners = [...ownerMap.values()].sort(
    (a, b) => (a.kind === b.kind ? 0 : a.kind === "airport" ? -1 : 1) || b.count - a.count,
  );

  const entered: SourceRef[] = [
    {
      kind: "entered",
      ref: "equipment register",
      label: t("the equipment register"),
      enteredBy: inp.register.enteredBy,
    },
  ];

  const tiles: FleetDesk["tiles"] = [
    {
      key: "total",
      label: t("items on the register"),
      value: rows.length,
      unit: "",
      text: t("{airport} the airport's, {other} a stakeholder's", {
        airport: rows.length - stakeholder.length,
        other: stakeholder.length,
      }),
      tone: "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "down",
      label: t("off the road"),
      value: down.length,
      unit: "",
      text: down.length
        ? t("{tags} — {parts} of them waiting for parts, which is a supplier's queue, not a crew's", {
            tags: down.map((r) => r.item.tag).join(", "),
            parts: awaitingParts.length,
          })
        : t("everything on the register is available"),
      tone: down.length ? "bad" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "permit",
      label: t("airside permit expired"),
      value: expiredPermit.length,
      unit: "",
      text: expiredPermit.length
        ? t("cannot go airside today: {tags}", { tags: expiredPermit.map((r) => r.item.tag).join(", ") })
        : t("every airside permit on the register is current"),
      tone: expiredPermit.length ? "bad" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "licence",
      label: t("license expired or expiring"),
      value: expiredLicence.length + soonLicence.length,
      unit: "",
      text: t("{expired} expired, {soon} within {days} days", {
        expired: expiredLicence.length,
        soon: soonLicence.length,
        days: EXPIRING_SOON_DAYS,
      }),
      tone: expiredLicence.length ? "bad" : soonLicence.length ? "warn" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "inspection",
      label: t("safety inspection overdue"),
      value: overdueInspection.length,
      unit: "",
      text: overdueInspection.length
        ? overdueInspection.map((r) => r.item.tag).join(", ")
        : t("no safety inspection is overdue"),
      tone: overdueInspection.length ? "bad" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "nodriver",
      label: t("no driver assigned"),
      value: noDriver.length,
      unit: "",
      text: noDriver.length
        ? noDriver.map((r) => r.item.tag).join(", ")
        : t("every item has somebody named against it"),
      tone: noDriver.length ? "warn" : "good",
      assumed: true,
      sources: entered,
    },
  ];

  const boardLine: string[] = [
    t("{site} holds {n} on the equipment register: {airport} the airport's own and {other} a stakeholder's.", {
      site: inp.siteCode,
      n: tn(rows.length, "{n} item", "{n} items"),
      airport: rows.length - stakeholder.length,
      other: stakeholder.length,
    }),
  ];
  if (expiredPermit.length || expiredLicence.length || overdueInspection.length) {
    boardLine.push(
      t(
        "Stopped today: {permit} with an expired airside permit, {licence} with an expired license, {inspection} with a safety inspection overdue.",
        { permit: expiredPermit.length, licence: expiredLicence.length, inspection: overdueInspection.length },
      ),
    );
  } else {
    boardLine.push(t("Nothing on the register is stopped by an expired permit, license or inspection today."));
  }
  if (down.length) {
    boardLine.push(
      t("{n} off the road: {list}.", {
        n: tn(down.length, "{n} item is", "{n} items are"),
        list: down.map((r) => `${r.item.tag} — ${r.item.conditionNote ?? t("no reason recorded")}`).join("; "),
      }),
    );
  }
  if (undated.length) {
    boardLine.push(
      t(
        "Missing a date: {n}. Check them: a blank date is not a valid permit.",
        { n: tn(undated.length, "{n} item", "{n} items") },
      ),
    );
  }

  const gaps: string[] = [
    inp.register.enteredBy || !inp.register.items.length
      ? t(
          "this register is entered by hand, so the platform doesn't watch its dates",
        )
      : t(
          "the maintenance log shows only what was entered here; platform work orders aren't merged in yet",
        ),
    t(
      "hours, fuel and use are recorded only where someone typed them, so service intervals can't be based on use",
    ),
  ];
  if (!rows.length) gaps.push(t("nothing is on the register yet"));

  const material = materialForFleet({ tiles, rows, boardLine });
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));

  return {
    generatedAt: inp.now.toISOString(),
    rows,
    byKind,
    owners,
    departments,
    tiles,
    boardLine,
    gaps,
    figures,
    enteredBy: inp.register.enteredBy,
    enteredAt: inp.register.enteredAt,
  };
}

export const FLEET_MODEL_PROMPT = [
  "You are advising an airport on its equipment register from the material given and nothing else.",
  "You may explain and prioritise; you must not state any number that is not in the material.",
  "Every figure here was entered by a person, not read from the platform. Say so when you use one.",
  "Never state that a vehicle is fit to go airside. That is the aerodrome's decision and it rests on permits you cannot see.",
  "Be brief and direct.",
].join(" ");

export function materialForFleet(d: Pick<FleetDesk, "tiles" | "rows" | "boardLine">): string {
  const L: string[] = [];
  for (const tile of d.tiles) L.push(`Tile ${tile.label}: ${tile.value ?? "n/a"} — ${tile.text} [entered]`);
  for (const row of d.rows) {
    const i = row.item;
    L.push(
      `${i.tag} ${i.name} (${KIND_LABEL[i.kind]}, ${i.owner.name}${i.owner.kind === "stakeholder" ? ", stakeholder" : ""}): ` +
        `${CONDITION_LABEL[i.condition]}${i.conditionNote ? ` — ${i.conditionNote}` : ""}; ` +
        `at ${i.location ?? "location not recorded"}; ` +
        `driver ${i.driver?.name ?? "none assigned"}; ` +
        `licence ${i.licenceExpiry ?? "not recorded"}; ` +
        `airside permit ${i.airsidePermit?.expiry ?? "not recorded"}; ` +
        `inspection due ${i.safetyInspection?.nextDueAt ?? "not scheduled"}; ` +
        `${row.maintenanceCount} maintenance entries, last ${row.lastMaintenanceAt ?? "never"}` +
        (row.flags.length ? `; FLAGS: ${row.flags.map((f) => f.what).join(", ")}` : ""),
    );
  }
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerFleet(q: string, d: FleetDesk): string | null {
  const text = q.toLowerCase();
  const list = (rows: FleetRow[], none: string) =>
    rows.length ? rows.map((r) => `${r.item.tag} ${r.item.name} — ${r.item.owner.name}`).join("\n") : none;

  if (/expir|licen[cs]e|permit|registration/.test(text)) {
    const bad = d.rows.filter((r) => r.flags.some((f) => f.kind === "expired" || f.kind === "expiring"));
    return list(
      bad,
      t("Nothing on the register has a license, permit or insurance expired or expiring within {days} days.", {
        days: EXPIRING_SOON_DAYS,
      }),
    );
  }
  if (/inspect|safety/.test(text)) {
    const bad = d.rows.filter((r) => r.flags.some((f) => f.kind === "overdue" || f.what.includes("inspection")));
    return list(bad, t("Every safety inspection on the register is scheduled and none is overdue."));
  }
  if (/driver|who drives|assigned/.test(text)) {
    const none = d.rows.filter((r) => !r.item.driver);
    return list(none, t("Every item on the register has a driver named against it."));
  }
  if (/unserviceable|broken|down|out of service/.test(text)) {
    const bad = d.rows.filter((r) => isDown(r.item.condition));
    return list(bad, t("Nothing on the register is unserviceable."));
  }
  if (/stakeholder|whose|owner|belongs/.test(text)) {
    return d.owners
      .map((o) =>
        t("{name}: {n}{down}", {
          name: o.name,
          n: tn(o.count, "{n} item", "{n} items"),
          down: o.down ? t(", {n} off the road", { n: o.down }) : "",
        }),
      )
      .join("\n");
  }
  if (/maintenance|serviced|last service/.test(text)) {
    const stale = [...d.rows].sort((a, b) => (a.lastMaintenanceAt ?? "").localeCompare(b.lastMaintenanceAt ?? ""));
    return stale
      .slice(0, 10)
      .map((r) =>
        t("{tag} {name}: {last}", {
          tag: r.item.tag,
          name: r.item.name,
          last: r.lastMaintenanceAt ?? t("nothing recorded"),
        }),
      )
      .join("\n");
  }
  if (/how many|count|total|register/.test(text)) return d.boardLine.join(" ");
  if (/board|summar/.test(text)) return d.boardLine.join(" ");
  return null;
}

/* ── the same register, read from the platform ────────────────────────── */

/**
 * The asset types the airports pack declares for vehicles and mobile plant.
 *
 * The register was entered data until the pack declared these. It now reads
 * from `/v1/assets`, and the entered copy is the fallback for a deployment
 * whose pack predates them — which is most of them, for a while.
 */
export const FLEET_ASSET_TYPES = [
  "airfield_sweeper",
  "surface_treatment_plant",
  "friction_tester",
  "tanker_vehicle",
  "earthmoving_plant",
  "access_platform",
  "haulage_vehicle",
  "rescue_appliance",
  "mobile_power_unit",
  "ground_support_equipment",
  "light_vehicle",
] as const;

/** Asset type key → the class this register groups it under. */
const KIND_OF_TYPE: Record<string, FleetKind> = {
  airfield_sweeper: "sweeper",
  surface_treatment_plant: "surface",
  friction_tester: "surface",
  tanker_vehicle: "tanker",
  earthmoving_plant: "earthmoving",
  access_platform: "access",
  haulage_vehicle: "haulage",
  rescue_appliance: "rescue",
  mobile_power_unit: "power",
  ground_support_equipment: "gse",
  light_vehicle: "vehicle",
};

/** One asset as `/v1/assets` returns it, narrowed to what this register reads. */
export interface FleetAssetRow {
  id: string;
  tag: string;
  name?: string;
  status?: string;
  assetType: string;
  zoneCode?: string;
  conditionScore?: number | null;
  attrs?: Record<string, unknown>;
  /** open defects against it, where the caller fetched them */
  openDefects?: { summary: string; severity: number; typeKey?: string }[];
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);

/**
 * Map the platform's assets into the register.
 *
 * Condition comes from the open defects, not from the asset's status. The
 * engine scopes every `on: asset` rule to in-service assets deliberately, so
 * a vehicle marked out of service silently leaves the licence, insurance and
 * permit rules — its licence then lapses in the workshop and nobody is told.
 * The platform's own seed therefore keeps a broken sweeper in service with a
 * defect against it, exactly as it does a defective runway, and this reads the
 * defect rather than the status.
 */
export function fleetFromAssets(rows: readonly FleetAssetRow[]): FleetRegister {
  const items: FleetItem[] = rows.map((row) => {
    const a = row.attrs ?? {};
    const defects = [...(row.openDefects ?? [])].sort((x, y) => y.severity - x.severity);
    const worst = defects[0];
    /*
     * The defect TYPE decides, not the severity.
     *
     * Reading severity alone collapsed "off the road" and "off the road with
     * parts on order" into one state, and they are the two that most need
     * separating: the first is a job a supervisor can schedule this week, the
     * second is a job nobody can schedule at all. The pack declares
     * `equipment_awaiting_parts` as its own defect type precisely so this can
     * tell them apart, and the severity is then only about how badly it is
     * wanted.
     */
    const waiting = defects.find((d) => d.typeKey === "equipment_awaiting_parts");
    const condition: Condition = waiting
      ? "awaiting_parts"
      : worst
        ? worst.severity >= 4
          ? "unserviceable"
          : "degraded"
        : "serviceable";
    const ownerKind = str(a.owner_kind) === "stakeholder" ? "stakeholder" : "airport";
    const driverName = str(a.driver_assigned);
    return {
      id: row.id,
      tag: row.tag,
      name: row.name ?? row.tag,
      kind: KIND_OF_TYPE[row.assetType] ?? "vehicle",
      owner: {
        kind: ownerKind,
        name: str(a.owner_org) ?? (ownerKind === "airport" ? "The airport" : "A stakeholder"),
      },
      make: str(a.make),
      model: str(a.model),
      year: num(a.year_of_manufacture),
      condition,
      conditionNote: (waiting ?? worst)?.summary,
      location: row.zoneCode,
      department: str(a.department),
      usedByDepartment: str(a.used_by_department),
      registration: str(a.registration),
      driver: driverName
        ? { name: driverName, permit: str(a.driver_permit_ref), permitExpiry: str(a.driver_permit_expiry_on) }
        : undefined,
      licenceExpiry: str(a.licence_expiry_on),
      insuranceExpiry: str(a.insurance_expiry_on),
      airsidePermit:
        str(a.airside_permit_ref) || str(a.airside_permit_expiry_on)
          ? { ref: str(a.airside_permit_ref), expiry: str(a.airside_permit_expiry_on) }
          : undefined,
      safetyInspection:
        str(a.safety_inspection_last_on) || str(a.safety_inspection_due_on)
          ? { lastAt: str(a.safety_inspection_last_on), nextDueAt: str(a.safety_inspection_due_on) }
          : undefined,
      hoursRun: num(a.hours_run),
      /* The maintenance log is the platform's work orders against the asset,
         fetched by the screen where it has them; not part of the asset row. */
      maintenance: [],
    };
  });
  return { items };
}
